<template>
  <!-- S03 · Hitos y finanzas: cabecera + hitos contractuales + hitos de pago con facturas anidadas +
       Change Orders (del PRM) + hitos penalizables. Orden, distribución y estilo según el nuevo diseño. -->
  <div class="s03">
    <!-- Cartillas superiores (mismo estilo): contrato/tipo de cambio + facturación, cobros e hitos. -->
    <div class="fin-boxes">
      <div class="fin-box">
        <div class="fin-title">{{ $t('prm_summary_s03_contract_box') }}</div>
        <div class="fin-row"><span class="k">{{ $t('prm_summary_fact_contract_amount') }} ({{ currency }})</span>
          <span class="v b">{{ fmtLocalAmt(contractAmountLocal) || '-' }}</span></div>
        <div class="fin-row"><span class="k">{{ $t('prm_summary_fact_rate') }}</span>
          <span class="v"><manual-field :value="s03.exchangeRate" :format="fmtRate" @save="onSaveNumber('s03.exchangeRate', $event)"></manual-field></span></div>
        <div class="fin-row"><span class="k">{{ $t('prm_summary_fact_exchange') }} ({{ currency }})</span>
          <span class="v"><manual-field :value="s03.exchangeInsurance" :format="fmtLocalAmt" @save="onSaveNumber('s03.exchangeInsurance', $event)"></manual-field></span></div>
        <div class="fin-row"><span class="k">{{ $t('prm_summary_fact_contract_amount') }} (€)</span>
          <span class="v b">{{ contractEur === '' ? '-' : eur(contractEur) }}</span></div>
        <div class="fin-row"><span class="k">{{ $t('prm_summary_fact_collection_days') }}</span>
          <span class="v"><manual-field :value="s03.collectionDays" :format="fmtDays" @save="onSave('s03.collectionDays', $event)"></manual-field></span></div>
      </div>

      <!-- Facturación, cobros e hitos: mismos datos que la tabla del Dashboard (plan de cobro real).
           En dos columnas (venta/facturación | cobro) para no crecer tanto en alto. -->
      <div class="fin-box billing">
        <div class="fin-title billing-title">
          <span>{{ $t('prm_dash_billing_title') }}</span>
          <sem-dot :state="billingCard.status || 'green'"></sem-dot>
        </div>
        <div class="fin-grid">
          <div class="fin-col">
            <div class="fin-row"><span class="k">{{ $t('prm_dash_billing_total_sale') }}</span><span class="v b">{{ eur(billingCard.totalSale) }}</span></div>
            <div class="fin-row"><span class="k">{{ $t('prm_dash_billing_invoiced') }}</span><span class="v b">{{ eur(billingCard.invoiced) }}</span></div>
            <div class="fin-row"><span class="k">{{ $t('prm_dash_billing_pending_invoice') }}</span><span class="v b">{{ eur(billingCard.pendingToInvoice) }}</span></div>
          </div>
          <div class="fin-col">
            <div class="fin-row"><span class="k">{{ $t('prm_dash_billing_collected') }}</span><span class="v b">{{ eur(billingCard.collected) }}</span></div>
            <div class="fin-row"><span class="k">{{ $t('prm_dash_billing_pending_collect') }}</span><span class="v b">{{ eur(billingCard.pendingToCollect) }}</span></div>
            <div class="fin-row"><span class="k">{{ $t('prm_dash_billing_overdue') }}</span>
              <span class="v b" :style="{ color: billingCard.overdue > 0 ? '#c0392b' : '#1e8449' }">{{ eur(billingCard.overdue) }}</span></div>
          </div>
        </div>
        <div class="fin-row wide"><span class="k">{{ $t('prm_dash_billing_next_milestone') }}</span><span class="v next">{{ nextMilestoneText }}</span></div>
      </div>
    </div>

    <!-- Hitos contractuales (tabla editable; añadir arriba, columna Acciones). -->
    <summary-card :title="$t('prm_summary_s03_contract_milestones')" :mode="mode" :mark="true" chip="none">
      <template #extra><button class="tbl-add" @click="$refs.contractTbl.addRow()"><b-icon pack="fas" icon="plus" size="is-small"></b-icon><span>{{ $t('prm_summary_s03_add_record') }}</span></button></template>
      <prm-summary-editable-table ref="contractTbl" :rows="s03.contractMilestones || []" :columns="contractColumns" :blank="blankContract"
        :mark="mode" :numbered="true" :number-label="$t('prm_summary_col_id')" :hide-footer="true" :actions-label="$t('prm_summary_s03_actions')"
        :remove-title="$t('prm_summary_remove_row')" :empty-text="$t('prm_summary_empty')" :reset-key="resetKey"
        @change="(items) => $emit('save-list', { path: 's03.contractMilestones', items })"></prm-summary-editable-table>
    </summary-card>

    <!-- Hitos de pago y facturación asociada: cada hito con modo Editar/Guardar y sus facturas anidadas. -->
    <summary-card :title="$t('prm_summary_s03_payment_milestones')" :subtitle="paySubtitle" :mode="mode">
      <template #extra>
        <button class="tbl-add" @click="addMilestone"><b-icon pack="fas" icon="plus" size="is-small"></b-icon><span>{{ $t('prm_summary_s03_pay_add') }}</span></button>
      </template>

      <div class="pm-list">
        <div v-for="(m, i) in localPaymentPlan" :key="m.id" class="pm-card">
          <div class="pm-head">
            <span class="pm-num">{{ i + 1 }}</span>
            <span class="pm-title">
              <prm-edit-field :editing="m._editing" :value="m.name" :bold="true" :placeholder="$t('prm_summary_s03_pay_name_ph')" @save="onMs(i, 'name', $event)"></prm-edit-field>
            </span>
            <span class="pm-sum">{{ pctPlain(m.porcentaje) }} · {{ fmtLocalAmt(m.importeLocal) }} · {{ eur(num(m.importeLocal) * rate) }}</span>
            <button class="pm-btn" :class="{ on: m._editing }" @click="toggleEdit(i)">
              <b-icon pack="fas" :icon="m._editing ? 'check' : 'pen'" size="is-small"></b-icon><span>{{ m._editing ? $t('prm_summary_s03_save') : $t('prm_summary_s03_edit') }}</span>
            </button>
            <button class="pm-toggle" @click="toggleInvoices(i)">
              <b-icon pack="fas" :icon="m._open ? 'chevron-up' : 'chevron-down'" size="is-small"></b-icon><span>{{ m._open ? $t('prm_summary_s03_inv_collapse') : $t('prm_summary_s03_inv_expand') }}</span>
            </button>
            <button class="pm-rm" v-tip="$t('prm_summary_remove_row')" @click="removeMilestone(i)"><b-icon pack="fas" icon="trash-can" size="is-small"></b-icon></button>
          </div>

          <div class="pm-fields">
            <div class="pm-f wide"><span class="pm-lbl">{{ $t('prm_summary_fact_condition') }}</span>
              <prm-edit-field :editing="m._editing" :value="m.condicionante" :dense="true" :bold="true" @save="onMs(i, 'condicionante', $event)"></prm-edit-field></div>
            <div class="pm-f"><span class="pm-lbl">{{ $t('prm_summary_fact_pct') }}</span>
              <prm-edit-field :editing="m._editing" :value="m.porcentaje" type="number" :dense="true" :bold="true" :format="pctPlain" @save="onMs(i, 'porcentaje', $event)"></prm-edit-field></div>
            <div class="pm-f"><span class="pm-lbl">{{ $t('prm_summary_fact_amount_local') }}</span>
              <prm-edit-field :editing="m._editing" :value="m.importeLocal" type="number" :dense="true" :bold="true" width="120px" :suffix="localSymbol" :format="fmtLocalAmt" @save="onMs(i, 'importeLocal', $event)"></prm-edit-field></div>
            <div class="pm-f"><span class="pm-lbl">{{ $t('prm_summary_s03_col_est_date') }}</span>
              <prm-edit-field :editing="m._editing" :value="m.fechaEstimada" type="date" :dense="true" :bold="true" :placeholder="$t('prm_summary_s03_date_ph')" @save="onMs(i, 'fechaEstimada', $event)"></prm-edit-field></div>
            <div class="pm-f"><span class="pm-lbl">{{ $t('prm_summary_s03_col_client_approval') }}</span>
              <prm-edit-field :editing="m._editing" :value="m.aprobacionCliente" type="date" :dense="true" :bold="true" :placeholder="$t('prm_summary_s03_date_ph')" @save="onMs(i, 'aprobacionCliente', $event)"></prm-edit-field></div>
            <div class="pm-f"><span class="pm-lbl">{{ $t('prm_summary_fact_notification') }}</span>
              <prm-edit-field :editing="m._editing" :value="m.notificacionCumplimiento" type="date" :dense="true" :bold="true" :placeholder="$t('prm_summary_s03_date_ph')" @save="onMs(i, 'notificacionCumplimiento', $event)"></prm-edit-field></div>
          </div>

          <!-- Facturas del hito (colapsadas por defecto), con su propio modo Editar/Guardar. -->
          <div v-if="m._open" class="inv-wrap">
            <div class="inv-head">
              <span class="inv-title">{{ $t('prm_summary_s03_inv_title') }} · {{ (m.invoices || []).length }}</span>
              <button class="pm-btn" :class="{ on: m._editingInv }" @click="toggleEditInv(i)"><b-icon pack="fas" :icon="m._editingInv ? 'check' : 'pen'" size="is-small"></b-icon><span>{{ m._editingInv ? $t('prm_summary_s03_save') : $t('prm_summary_s03_inv_edit') }}</span></button>
              <button class="inv-add" @click="addInvoice(i)"><b-icon pack="fas" icon="plus" size="is-small"></b-icon><span>{{ $t('prm_summary_s03_inv_add') }}</span></button>
            </div>
            <div class="inv-scroll">
              <table class="inv-table">
                <thead>
                  <tr>
                    <th>{{ $t('prm_summary_s03_inv_issue') }}</th><th>{{ $t('prm_summary_s03_inv_number') }}</th>
                    <th class="r">{{ $t('prm_summary_fact_amount_local') }}</th><th class="r">{{ $t('prm_summary_s03_inv_eur') }}</th>
                    <th class="c">{{ $t('prm_summary_s03_inv_due') }}</th><th>{{ $t('prm_summary_s03_col_payment_date') }}</th>
                    <th class="c">{{ $t('prm_summary_s03_inv_status') }}</th><th class="c">{{ $t('prm_summary_collected') }}</th>
                    <th class="r">{{ $t('prm_summary_s03_col_collected_amount') }}</th><th>{{ $t('prm_summary_s03_col_notes') }}</th>
                    <th class="c">{{ $t('prm_summary_s03_inv_pdf') }}</th><th v-if="m._editingInv" class="act">{{ $t('prm_summary_s03_actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(inv, j) in (m.invoices || [])">
                  <tr :key="inv.id">
                    <td><prm-edit-field :editing="m._editingInv" :value="inv.emision" type="date" :dense="true" width="128px" :placeholder="$t('prm_summary_s03_date_ph')" @save="onInv(i, j, 'emision', $event)"></prm-edit-field></td>
                    <td><prm-edit-field :editing="m._editingInv" :value="inv.invoiceNumber" :dense="true" width="96px" @save="onInv(i, j, 'invoiceNumber', $event)"></prm-edit-field></td>
                    <td class="r"><prm-edit-field :editing="m._editingInv" :value="inv.importeLocal" type="number" :dense="true" width="112px" :suffix="localSymbol" :format="fmtLocalAmt" @save="onInv(i, j, 'importeLocal', $event)"></prm-edit-field></td>
                    <td class="r eur">{{ eur(num(inv.importeLocal) * rate) }}</td>
                    <td class="c calc">{{ dueDate(inv) || '-' }}</td>
                    <td><prm-edit-field :editing="m._editingInv" :value="inv.fechaPago" type="date" :dense="true" width="128px" :placeholder="$t('prm_summary_s03_date_ph')" @save="onInv(i, j, 'fechaPago', $event)"></prm-edit-field></td>
                    <td class="c"><span class="vbadge" :class="vencimiento(inv)">{{ $t('prm_summary_s03_venc_' + vencimiento(inv)) }}</span></td>
                    <td class="c"><prm-edit-field :editing="m._editingInv" :value="inv.cobrado" type="select" :dense="true" width="56px" :options="ynOptions" @save="onInv(i, j, 'cobrado', $event)"></prm-edit-field></td>
                    <td class="r"><prm-edit-field :editing="m._editingInv" :value="inv.importeCobrado" type="number" :dense="true" width="112px" :suffix="localSymbol" :format="fmtLocalAmt" @save="onInv(i, j, 'importeCobrado', $event)"></prm-edit-field></td>
                    <td><prm-edit-field :editing="m._editingInv" :value="inv.observaciones" :dense="true" width="160px" @save="onInv(i, j, 'observaciones', $event)"></prm-edit-field></td>
                    <td class="c"><prm-versioned-file :file="fileFor('invoice', inv.id)" :editing="true" :project-id="projectId"
                      :open="invHistory === inv.id" :upload-url="uploadUrl('invoice', inv.id)"
                      @uploaded="$emit('files-changed')" @toggle-history="invHistory = invHistory === inv.id ? null : inv.id"></prm-versioned-file></td>
                    <td v-if="m._editingInv" class="act"><button class="rm" @click="removeInvoice(i, j)"><b-icon pack="fas" icon="trash-can" size="is-small"></b-icon></button></td>
                  </tr>
                  <tr v-if="invHistory === inv.id && fileFor('invoice', inv.id)" :key="inv.id + '-h'" class="hist-tr">
                    <td :colspan="m._editingInv ? 12 : 11"><prm-file-history :file="fileFor('invoice', inv.id)" :base-url="baseUrl" :project-id="projectId" @close="invHistory = null"></prm-file-history></td>
                  </tr>
                  </template>
                  <tr v-if="!(m.invoices || []).length"><td class="empty" :colspan="m._editingInv ? 12 : 11">{{ $t('prm_summary_empty') }}</td></tr>
                </tbody>
              </table>
            </div>
            <div class="inv-total">
              <span><b>{{ $t('prm_summary_s03_inv_total_invoices') }}:</b> {{ fmtLocalAmt(invTotal(m)) }}</span>
              <span><b>{{ $t('prm_summary_s03_inv_equivalence') }}:</b> {{ eur(invTotal(m) * rate) }}</span>
            </div>
          </div>
        </div>
        <div v-if="!localPaymentPlan.length" class="pm-empty">{{ $t('prm_summary_empty') }}</div>
      </div>
    </summary-card>

    <!-- Change Orders (del PRM · coste y status de Control económico; origen/plazo/PDF editables aquí) -->
    <summary-card :title="$t('prm_summary_s03_change_orders')" :subtitle="coSubtitle" :mode="mode" chip="prm">
      <div class="co-scroll">
        <table class="co-table">
          <thead>
            <tr>
              <th>{{ $t('prm_summary_s03_co_order') }}</th>
              <th>{{ $t('prm_summary_s03_co_origin') }}</th>
              <th class="r">{{ $t('prm_summary_s03_co_cost') }}</th>
              <!-- Columna "Plazo" oculta de momento: <th>{{ $t('prm_summary_s03_co_term') }}</th> -->
              <th class="c">{{ $t('prm_summary_col_status') }}</th>
              <th class="c">{{ $t('prm_summary_s02_pdf_col') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="co in changeOrders">
            <tr :key="co.code">
              <td class="co-code">{{ co.code }}</td>
              <td class="co-in"><prm-edit-field :editing="true" :value="co.origen" type="select" :dense="true" width="90px" :options="originOptions" @save="onCo(co.code, 'origen', $event)"></prm-edit-field></td>
              <td class="r b">{{ eur(co.cost) }}</td>
              <!-- Columna "Plazo" oculta de momento: <td class="co-in"><prm-edit-field :editing="true" :value="co.plazo" :dense="true" width="96px" :placeholder="$t('prm_summary_s03_co_term_ph')" @save="onCo(co.code, 'plazo', $event)"></prm-edit-field></td> -->
              <td class="c"><span class="co-status" :class="co.status">{{ $t('prm_summary_s03_co_status_' + co.status) }}</span></td>
              <td class="c"><prm-versioned-file :file="fileFor('change_order', co.code)" :editing="true" :project-id="projectId"
                :open="coHistory === co.code" :upload-url="uploadUrl('change_order', co.code)"
                @uploaded="$emit('files-changed')" @toggle-history="coHistory = coHistory === co.code ? null : co.code"></prm-versioned-file></td>
            </tr>
            <tr v-if="coHistory === co.code && fileFor('change_order', co.code)" :key="co.code + '-h'" class="hist-tr">
              <td colspan="5"><prm-file-history :file="fileFor('change_order', co.code)" :base-url="baseUrl" :project-id="projectId" @close="coHistory = null"></prm-file-history></td>
            </tr>
            </template>
            <tr v-if="!changeOrders.length"><td class="empty" colspan="5">{{ $t('prm_summary_s03_co_none') }}</td></tr>
          </tbody>
        </table>
      </div>
    </summary-card>

    <!-- Hitos penalizables (penalidad calculada y penalizando automáticos; añadir arriba, columna Acciones). -->
    <summary-card :title="$t('prm_summary_s03_penalty_milestones')" :mode="mode" :mark="true" chip="none">
      <template #extra>
        <span class="pen-total" :class="{ red: penaltyTotalValue > 0 }">{{ $t('prm_summary_s03_penalty_total') }}: {{ eur(penaltyTotalValue) }} · {{ $t('prm_summary_s03_penalty_note') }}</span>
        <button class="tbl-add" @click="$refs.penaltyTbl.addRow()"><b-icon pack="fas" icon="plus" size="is-small"></b-icon><span>{{ $t('prm_summary_s03_add_record') }}</span></button>
      </template>
      <prm-summary-editable-table ref="penaltyTbl" :rows="s03.penaltyMilestones || []" :columns="penaltyColumns" :blank="blankPenalty"
        :mark="mode" :numbered="true" :number-label="$t('prm_summary_col_id')" :scroll="true" :hide-footer="true" :actions-label="$t('prm_summary_s03_actions')"
        :remove-title="$t('prm_summary_remove_row')" :empty-text="$t('prm_summary_empty')" :reset-key="resetKey"
        @change="(items) => $emit('save-list', { path: 's03.penaltyMilestones', items })"></prm-summary-editable-table>
    </summary-card>
  </div>
</template>

<script>
  import SummaryCard from './summary-card.vue'
  import ManualField from './manual-field.vue'
  import PrmSummaryEditableTable from './editable-table.vue'
  import PrmVersionedFile from './versioned-file.vue'
  import PrmFileHistory from './file-history.vue'
  import PrmEditField from './edit-field.vue'
  import SemDot from '../dashboard/sem-dot.vue'
  import { billingCardData } from './summary-model'
  import { fmtEur, fmtLocalAmount, currencySymbol, fmtDate } from '../shared/format'
  import { tip } from '../shared/tip'

  const nfRate = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  let seq = 0
  function genId () { return 'r' + Date.now().toString(36) + (seq++).toString(36) + Math.random().toString(36).slice(2, 6) }

  export default {
    name: 'PrmSummaryS03',

    components: { SummaryCard, ManualField, PrmSummaryEditableTable, PrmVersionedFile, PrmFileHistory, PrmEditField, SemDot },

    directives: { tip },

    props: {
      doc: { type: Object, required: true },
      sources: { type: Object, required: true },
      project: { type: Object, default: () => ({}) },
      billing: { type: Object, default: () => ({}) },
      mode: { type: Boolean, default: false },
      resetKey: { type: Number, default: 0 },
    },

    data: () => ({ localPaymentPlan: [], invHistory: null, coHistory: null, today: new Date().toISOString().slice(0, 10) }),

    computed: {
      s03 () { return this.doc.s03 || {} },
      projectId () { return this.project && this.project.id },
      baseUrl () { return `/api/v2/project/${this.projectId}/dashboard-prm` },
      currency () { return this.s03.contractLocalCurrency || 'EUR' },
      localSymbol () { return currencySymbol(this.currency) },
      paySubtitle () { return `${this.localPaymentPlan.length} ${this.$t('prm_summary_s03_pay_registered')}` },
      rate () { const r = Number(this.s03.exchangeRate); return Number.isFinite(r) && r > 0 ? r : 1 },
      totalAmount () { return (this.sources.prm && this.sources.prm.totalAmount) || 0 },
      contractAmountLocal () { const v = this.s03.contractAmountLocal; return (v === null || v === undefined) ? '' : v },
      contractEur () { const a = this.s03.contractAmountLocal; return (a === null || a === undefined || a === '') ? '' : (Number(a) || 0) * this.rate },

      // Cartilla "Facturación, cobros e hitos": misma fuente que la tabla del Dashboard (plan de cobro real).
      billingCard () { return billingCardData(this.s03, this.totalAmount, this.today) },
      nextMilestoneText () {
        const m = this.billingCard.nextMilestone
        if (!m) return '-'
        const date = m.estimatedDate ? ` · ${fmtDate(m.estimatedDate)}` : ''
        return `${m.description || '-'}${date}`
      },

      contractFiles () { return this.sources.contractFiles || {} },

      // Change Orders: base del PRM (coste + status) + overlay editable (origen/plazo) de s03.changeOrders.
      // Solo se muestran las COs que ya tienen coste (se ocultan las que están a 0).
      changeOrders () {
        const overlay = {}
        ;(this.s03.changeOrders || []).forEach((o) => { if (o && o.code) overlay[o.code] = o })
        return (this.sources.changeOrders || [])
          .filter((co) => Number(co.cost) !== 0)
          .map((co) => ({
            code: co.code,
            cost: co.cost,
            status: co.status,
            origen: (overlay[co.code] || {}).origen || '',
            plazo: (overlay[co.code] || {}).plazo || '',
          }))
      },
      coSubtitle () { return `${this.changeOrders.length} ${this.$t('prm_summary_s03_co_registered')}` },

      pmSerOptions () { return [{ value: 'PM', label: 'PM' }, { value: 'SER', label: 'SER' }] },
      milestoneStatusOptions () { return [{ value: 'open', label: this.$t('prm_summary_risk_status_open') }, { value: 'closed', label: this.$t('prm_summary_risk_status_closed') }] },
      ynOptions () { return ['yes', 'no'].map((v) => ({ value: v, label: this.$t('prm_summary_' + v) })) },
      originOptions () { return [{ value: 'Cliente', label: 'Cliente' }, { value: 'TKAS', label: 'TKAS' }] },

      contractColumns () {
        return [
          { key: 'description', label: this.$t('prm_summary_col_description'), type: 'text', width: '30%' },
          { key: 'date', label: this.$t('prm_summary_s04_col_date'), type: 'date', align: 'center' },
          { key: 'owner', label: this.$t('prm_summary_col_owner'), type: 'select', align: 'center', options: this.pmSerOptions },
          { key: 'status', label: this.$t('prm_summary_col_status'), type: 'select', align: 'center', options: this.milestoneStatusOptions, cellClass: (r) => 'st-badge st-' + (r.status || 'open') },
          { key: 'comments', label: this.$t('prm_summary_s04_col_comments'), type: 'text' },
        ]
      },
      blankContract () { return { description: '', date: '', owner: 'PM', status: 'open', comments: '' } },

      penaltyColumns () {
        return [
          { key: 'description', label: this.$t('prm_summary_col_description'), type: 'text', width: '20%' },
          { key: 'contractDate', label: this.$t('prm_summary_s03_col_contract_date'), type: 'date', align: 'center' },
          { key: 'complianceDate', label: this.$t('prm_summary_s03_col_compliance_date'), type: 'date', align: 'center' },
          { key: 'penalty', label: this.$t('prm_summary_s03_col_penalty'), type: 'text', align: 'center' },
          { key: 'daysDiff', label: this.$t('prm_summary_s03_col_days'), type: 'text', align: 'center' },
          { key: 'calculated', label: this.$t('prm_summary_s03_col_calculated'), type: 'readonly', align: 'right', format: (r) => fmtEur(this.calcPenalty(r)) },
          { key: 'penalizing', label: this.$t('prm_summary_s03_col_penalizing'), type: 'readonly', align: 'center', format: (r) => this.$t('prm_summary_' + (this.calcPenalty(r) > 0 ? 'yes' : 'no')), cellClass: (r) => this.calcPenalty(r) > 0 ? 'yn-no' : 'yn-yes' },
          { key: 'comments', label: this.$t('prm_summary_s04_col_comments'), type: 'text', width: '200px' },
        ]
      },
      blankPenalty () { return { description: '', contractDate: '', complianceDate: '', penalty: '', daysDiff: null, comments: '' } },

      penaltyTotalValue () {
        const sum = (this.s03.penaltyMilestones || []).reduce((s, r) => s + this.calcPenalty(r), 0)
        const capPct = Number((this.doc.meta || {}).penaltyCapPct) || 0
        const cap = this.totalAmount * (capPct / 100)
        return cap > 0 ? Math.min(sum, cap) : sum
      },
    },

    watch: {
      resetKey: { immediate: true, handler () { this.initPaymentPlan() } },
    },

    methods: {
      num (v) { return Number(v) || 0 },
      eur (v) { return fmtEur(v) },
      fmtLocalAmt (v) { return (v === '' || v == null) ? '' : fmtLocalAmount(v, this.currency) },
      fmtRate (v) { return nfRate.format(this.num(v)) },
      fmtDays (v) { return `${this.num(v)} ${this.$t('prm_summary_fact_days')}` },
      pctPlain (v) { return `${this.num(v)} %` },
      onSave (path, value) { this.$emit('save-field', { path, value }) },
      // Guarda un valor numérico normalizando la coma decimal a punto (siempre se persiste con punto).
      // Acepta "1,25" y "1.234,56" (formato es-ES) o "1234.56"; vacío -> null.
      onSaveNumber (path, raw) { this.onSave(path, this.parseNum(raw)) },
      parseNum (raw) {
        if (raw === '' || raw === null || raw === undefined) return null
        let s = String(raw).trim()
        // Coma y punto juntos: punto = miles, coma = decimal. Solo coma: decimal.
        s = (s.includes(',') && s.includes('.')) ? s.replace(/\./g, '').replace(',', '.') : s.replace(',', '.')
        const n = Number(s)
        return Number.isFinite(n) ? n : null
      },

      fileFor (collection, rowId) { return ((this.contractFiles[collection] || {})[rowId]) || null },
      uploadUrl (collection, rowId) { return `${this.baseUrl}/contract-files/${collection}/${rowId}` },

      // Penalidad calculada = (tasa % de la penalización) x importe total x días de diferencia.
      calcPenalty (r) {
        const days = this.num(r.daysDiff)
        if (days <= 0) return 0
        const m = /([\d.,]+)\s*%/.exec(r.penalty || '')
        const pct = m ? parseFloat(m[1].replace(',', '.')) : 0
        return (pct / 100) * this.totalAmount * days
      },

      // Fechas en formato DD/MM/AAAA (como el diseño). Parseo/formateo para el vencimiento.
      parseDMY (s) {
        const m = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/.exec((s || '').trim())
        if (!m) return null
        let y = Number(m[3]); if (y < 100) y += 2000
        const d = new Date(y, Number(m[2]) - 1, Number(m[1]))
        return isNaN(d.getTime()) ? null : d
      },
      fmtDMY (d) { const p = (n) => String(n).padStart(2, '0'); return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}` },
      // Vencimiento de la factura = emisión + plazo de cobro.
      dueDate (inv) {
        const d = this.parseDMY(inv.emision)
        if (!d) return ''
        d.setDate(d.getDate() + this.num(this.s03.collectionDays))
        return this.fmtDMY(d)
      },
      vencimiento (inv) {
        const d = this.parseDMY(inv.emision)
        if (!d || inv.cobrado === 'yes') return 'no'
        d.setDate(d.getDate() + this.num(this.s03.collectionDays))
        return d < new Date() ? 'yes' : 'no'
      },
      invTotal (m) { return (m.invoices || []).reduce((s, inv) => s + this.num(inv.importeLocal), 0) },

      // Hitos de pago (copia local + autosave; modo Editar/Guardar por hito y por facturas)
      initPaymentPlan () {
        this.localPaymentPlan = (this.s03.paymentPlan || []).map((m) => ({
          ...m,
          id: m.id || genId(),
          invoices: (m.invoices || []).map((inv) => ({ ...inv, id: inv.id || genId() })),
          _open: false, _editing: false, _editingInv: false,
        }))
      },
      toggleEdit (i) { this.localPaymentPlan[i]._editing = !this.localPaymentPlan[i]._editing },
      toggleEditInv (i) { this.localPaymentPlan[i]._editingInv = !this.localPaymentPlan[i]._editingInv },
      toggleInvoices (i) { this.localPaymentPlan[i]._open = !this.localPaymentPlan[i]._open },
      onMs (i, key, value) { this.localPaymentPlan[i][key] = value; this.emitPlan() },
      addMilestone () {
        this.localPaymentPlan.push({ id: genId(), name: '', condicionante: '', porcentaje: null, importeLocal: null, fechaEstimada: '', aprobacionCliente: '', notificacionCumplimiento: '', invoices: [], _open: true, _editing: true, _editingInv: false })
        this.emitPlan()
      },
      removeMilestone (i) {
        // Al borrar el hito, borramos también el fichero (con todas sus versiones) de CADA una de
        // sus facturas, para no dejar documentos huérfanos en el bucket.
        const invoices = this.localPaymentPlan[i].invoices || []
        invoices.forEach((inv) => {
          const f = this.fileFor('invoice', inv.id)
          if (f) this.$emit('remove-file', f.id)
        })
        this.localPaymentPlan.splice(i, 1)
        this.emitPlan()
      },
      addInvoice (i) {
        this.localPaymentPlan[i].invoices.push({ id: genId(), emision: '', invoiceNumber: '', importeLocal: null, fechaPago: '', cobrado: 'no', importeCobrado: null, observaciones: '' })
        this.localPaymentPlan[i]._editingInv = true
        this.emitPlan()
      },
      removeInvoice (i, j) {
        const inv = this.localPaymentPlan[i].invoices[j]
        this.localPaymentPlan[i].invoices.splice(j, 1)
        this.emitPlan()
        if (inv && this.fileFor('invoice', inv.id)) this.$emit('remove-file', this.fileFor('invoice', inv.id).id)
      },
      onInv (i, j, key, value) { this.localPaymentPlan[i].invoices[j][key] = value; this.emitPlan() },
      emitPlan () {
        const clean = this.localPaymentPlan.map((m) => {
          const c = { ...m }; delete c._open; delete c._editing; delete c._editingInv
          c.invoices = (c.invoices || []).map((inv) => ({ ...inv }))
          return c
        })
        this.$emit('save-list', { path: 's03.paymentPlan', items: clean })
      },

      // Change Orders (overlay origen/plazo por codigo)
      onCo (code, key, value) {
        const list = (this.s03.changeOrders || []).map((o) => ({ ...o }))
        let row = list.find((o) => o.code === code)
        if (!row) { row = { code, origen: '', plazo: '' }; list.push(row) }
        row[key] = value
        this.$emit('save-list', { path: 's03.changeOrders', items: list })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .s03 { display: flex; flex-direction: column; gap: 10px; }

  // Cartillas superiores (contrato + facturación): dos columnas, apiladas en pantallas estrechas.
  .fin-boxes { display: flex; gap: 12px; align-items: flex-start; flex-wrap: wrap; }
  // Cada cartilla: label izquierda, valor derecha (en negrita los importes), como el diseño.
  .fin-box { flex: 1 1 300px; max-width: 440px; border: 1px solid #d4dae3; border-radius: 6px; background: #fff; padding: 0 0 4px; }
  .fin-title {
    padding: 6px 12px 5px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em;
    color: #3d6b9a; border-bottom: 1px solid #eef0f3; margin-bottom: 3px;
  }
  .fin-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 3px 12px; font-size: 11px; }
  .fin-row .k { color: #6b7787; }
  .fin-row .v { color: #1e3046; text-align: right; }
  .fin-row .v.b { font-weight: 700; font-variant-numeric: tabular-nums; }
  .fin-row .v.next { font-size: 10px; white-space: normal; }

  // Cartilla de facturación en dos columnas: más ancha, con separador central y el semáforo en el título.
  .fin-box.billing { flex: 1.3 1 360px; max-width: 560px; }
  .billing-title { display: flex; align-items: center; justify-content: space-between; }
  .fin-grid { display: grid; grid-template-columns: 1fr 1fr; }
  .fin-col + .fin-col { border-left: 1px solid #eef0f3; }
  .fin-grid .fin-row { gap: 8px; padding: 3px 10px; }
  // Próximo hito: fila a lo ancho bajo las dos columnas.
  .fin-row.wide { border-top: 1px solid #eef0f3; margin-top: 2px; padding-top: 5px; }

  // Botón "Añadir ..." en la cabecera de sección (arriba, como el diseño).
  .tbl-add { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #859bb2; background: #fff; color: #2f5f8f; font-size: 10px; font-weight: 600; padding: 4px 10px; border-radius: 4px; cursor: pointer; white-space: nowrap; }
  .tbl-add:hover { background: #f4f8fc; }

  // Hitos de pago con facturas anidadas.
  .pm-list { display: flex; flex-direction: column; gap: 8px; padding: 10px; }
  .pm-card { border: 1px solid #eef0f3; border-radius: 6px; overflow: hidden; }
  .pm-head { display: flex; align-items: center; gap: 8px; padding: 7px 10px; background: #f6f8fa; border-bottom: 1px solid #eef0f3; }
  .pm-num { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 4px; background: #1e3046; color: #fff; font-size: 10px; font-weight: 700; flex-shrink: 0; }
  .pm-title { flex: 1 1 200px; min-width: 120px; }
  .pm-title ::v-deep .ef-val { font-size: 11px; font-weight: 700; color: #1e3046; }
  .pm-title ::v-deep .ef-val.empty { font-weight: 400; color: #9aa4b2; } // texto guia del hito sin nombre
  .pm-sum { font-size: 10px; font-weight: 600; color: #425b76; }
  .pm-btn { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #9fb0c2; background: #fff; color: #425b76; font-size: 10px; font-weight: 600; padding: 4px 10px; border-radius: 4px; cursor: pointer; white-space: nowrap; }
  .pm-btn:hover { background: #f4f8fc; }
  .pm-btn.on { color: #1e8449; border-color: #79ad8b; background: #edf8f0; }
  .pm-btn:first-of-type { margin-left: auto; }
  // Botón chevron (Desplegar/Replegar facturas).
  .pm-toggle { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #9fb0c2; background: #fff; color: #2f5f8f; font-size: 10px; font-weight: 600; padding: 4px 10px; border-radius: 4px; cursor: pointer; white-space: nowrap; }
  .pm-toggle:hover { background: #f4f8fc; }
  .pm-rm { display: inline-flex; align-items: center; border: 0; background: transparent; color: #9ca3af; cursor: pointer; padding: 2px; }
  .pm-rm:hover { color: #dc2626; }

  // Campos del hito: grid con borde, label arriba y valor/input abajo (como el diseño).
  .pm-fields { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr; border-top: 1px solid #eef0f3; }
  .pm-f { display: flex; flex-direction: column; gap: 3px; padding: 8px 10px; border-left: 1px solid #eef0f3; min-width: 0; }
  .pm-f:first-child { border-left: 0; }
  .pm-lbl { font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; color: #7a8798; }
  .pm-empty { text-align: center; color: #9aa4b2; font-size: 11px; padding: 10px; }

  .inv-wrap { border-top: 1px solid #eef0f3; background: #fbfcfe; padding: 8px 10px; }
  .inv-head { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
  .inv-title { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #6b7787; margin-right: auto; }
  .inv-add { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #9fb5cc; background: #eef5fc; color: #2f5f8f; font-size: 10px; font-weight: 600; padding: 4px 10px; border-radius: 4px; cursor: pointer; white-space: nowrap; }
  .inv-add:hover { background: #e3eefa; }
  .inv-scroll { overflow-x: auto; }
  .inv-table { width: 100%; border-collapse: collapse; }
  .inv-table th { background: #557a9f; color: #fff; font-size: 9px; font-weight: 600; padding: 6px 8px; text-align: left; white-space: nowrap; }
  .inv-table th.r { text-align: right; }
  .inv-table th.c { text-align: center; }
  .inv-table td { border-bottom: 1px solid #eef0f3; padding: 3px 6px; font-size: 10px; color: #1f2837; vertical-align: middle; white-space: nowrap; }
  .inv-table td.r { text-align: right; }
  .inv-table td.c { text-align: center; }
  .inv-table td.eur { background: #eef3fb; font-weight: 600; }
  .inv-table td.calc { background: #eef3fb; color: #33475f; }
  .inv-table td.empty { text-align: center; color: #9aa4b2; padding: 10px; }
  .inv-table .act { width: 40px; text-align: center; }
  .inv-table .rm { display: inline-flex; align-items: center; border: 0; background: transparent; color: #9ca3af; cursor: pointer; padding: 2px; }
  .inv-table .rm:hover { color: #dc2626; }
  // Vencimiento: texto plano coloreado (sin fondo), como el diseño.
  .vbadge { font-size: 10px; font-weight: 600; }
  .vbadge.no { color: #1e8449; }
  .vbadge.yes { color: #dc2626; }
  .inv-total { display: flex; flex-wrap: wrap; gap: 4px 18px; padding: 6px 2px 0; font-size: 11px; color: #2c3a4d; }

  // Change Orders.
  .co-scroll { overflow-x: auto; }
  .co-table { width: 100%; border-collapse: collapse; }
  .co-table th { background: #3d6b9a; color: #fff; font-size: 10px; font-weight: 700; padding: 8px 12px; text-align: left; white-space: nowrap; }
  .co-table th.r { text-align: right; } .co-table th.c { text-align: center; }
  .co-table td { border-bottom: 1px solid #eef0f3; padding: 6px 12px; font-size: 11px; color: #1f2837; vertical-align: middle; white-space: nowrap; }
  .co-table td.r { text-align: right; } .co-table td.c { text-align: center; } .co-table td.b { font-weight: 600; }
  .co-table td.co-code { font-weight: 700; color: #1e3046; }
  .co-table td.co-in { min-width: 100px; }
  .co-table td.empty { text-align: center; color: #9aa4b2; padding: 10px; }
  // Fila del histórico documental desplegado bajo la factura / change order.
  .inv-table .hist-tr td, .co-table .hist-tr td { padding: 6px 10px; background: #fbfcfe; }
  .co-status { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 600; }
  .co-status.confirmed { background: #eaf6ee; color: #237a43; }
  .co-status.pending { background: #fdf3d7; color: #b7791f; }

  .pen-total { margin-left: auto; font-size: 9px; font-weight: 400; text-transform: none; color: #6b7787; }
  .pen-total.red { color: #c0392b; font-weight: 600; }
</style>
